import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { fetchNewsItem, fetchBlogComments, postBlogComment } from "../lib/api";

export default function BlogDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(false);
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ nome: "", email: "", messaggio: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchNewsItem(id).then(setItem).catch(() => setErr(true));
    fetchBlogComments(id).then(setComments).catch(() => {});
  }, [id]);

  const submitComment = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postBlogComment({ news_id: id, ...form });
      toast.success("Grazie! Il tuo commento sarà pubblicato dopo l'approvazione della redazione.");
      setForm({ nome: "", email: "", messaggio: "" });
    } catch {
      toast.error("Invio non riuscito, riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  if (err) {
    return (
      <div className="py-32 px-6 text-center" data-testid="blog-detail-error">
        <h1 className="font-serif-display text-4xl text-[#A3133A] mb-4">Articolo non trovato</h1>
        <Link to="/blog" className="text-[12px] tracking-eyebrow text-[#A3133A] link-underline">
          Torna al blog
        </Link>
      </div>
    );
  }

  if (!item) {
    return <div className="py-32 text-center text-[#5C5C5C]">Caricamento...</div>;
  }

  return (
    <article data-testid="page-blog-detail" className="py-16 px-6 md:px-12 lg:px-24 max-w-[1000px] mx-auto">
      <Link to="/blog" data-testid="blog-back" className="text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2 mb-8 link-underline">
        <ArrowLeft size={14} /> Tutti gli articoli
      </Link>

      <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">
        {item.categoria} · {new Date(item.data).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
      </div>

      <h1 className="font-serif-display text-4xl sm:text-6xl text-[#1C1C1C] leading-[1.05] mb-10">{item.titolo}</h1>

      <div className="aspect-[16/9] overflow-hidden mb-10 border border-[#A3133A]/15">
        <img src={item.immagine} alt={item.titolo} className="w-full h-full object-cover" />
      </div>

      <div className="prose text-[#1C1C1C] text-lg leading-relaxed">
        <p className="text-xl text-[#5C5C5C] italic mb-6">{item.estratto}</p>
        <p className="whitespace-pre-line">{item.contenuto}</p>
      </div>

      <div className="mt-16 pt-12 border-t border-[#A3133A]/15" data-testid="blog-comments-section">
        <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4 flex items-center gap-2">
          <MessageCircle size={14} /> Commenti ({comments.length})
        </div>

        {comments.length > 0 && (
          <div className="flex flex-col gap-4 mb-10">
            {comments.map((c) => (
              <div key={c.id} className="bg-[#FBFBF9] border border-[#A3133A]/10 p-5" data-testid={`blog-comment-${c.id}`}>
                <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-2">
                  {c.nome} · {new Date(c.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                </div>
                <p className="text-sm text-[#1C1C1C] leading-relaxed">{c.messaggio}</p>
              </div>
            ))}
          </div>
        )}

        <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-2">Lascia un commento</h3>
        <p className="text-xs text-[#5C5C5C] mb-5" data-testid="comment-moderation-note">
          I commenti sono moderati: saranno visibili dopo l'approvazione della redazione.
        </p>
        <form onSubmit={submitComment} className="flex flex-col gap-4 max-w-xl" data-testid="blog-comment-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              required
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Il tuo nome *"
              data-testid="comment-nome-input"
              className="border border-[#A3133A]/25 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#A3133A] rounded-sm"
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="La tua email * (non sarà pubblicata)"
              data-testid="comment-email-input"
              className="border border-[#A3133A]/25 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#A3133A] rounded-sm"
            />
          </div>
          <textarea
            required
            rows={4}
            value={form.messaggio}
            onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
            placeholder="Il tuo commento *"
            data-testid="comment-messaggio-input"
            className="border border-[#A3133A]/25 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#A3133A] rounded-sm"
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="comment-submit-button"
            className="self-start bg-[#A3133A] text-white px-7 py-3.5 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#8B0F2F] transition inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Send size={13} /> {sending ? "Invio in corso..." : "Pubblica commento"}
          </button>
        </form>
      </div>
    </article>
  );
}
